import { Link } from "@tanstack/react-router";
import { ChevronsUpDown, KeyRound, LogOut, Settings2, ShieldCheck, UserRound } from "lucide-react";
import { useSession } from "@/lib/session.tsx";
import { initials, titleCase } from "@/lib/utils.ts";
import { Avatar, AvatarFallback } from "@/components/ui/avatar.tsx";
import { Button } from "@/components/ui/button.tsx";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu.tsx";
import { Badge } from "@/components/ui/badge.tsx";

export function UserMenu({ compact = false }: { compact?: boolean }) {
  const { me, signOut } = useSession();
  if (!me) return null;
  const roleLabel = titleCase(me.role);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compact ? (
          <Button variant="ghost" size="icon" aria-label="Account menu" className="rounded-full">
            <Avatar size="default">
              <AvatarFallback>{initials(me.label || me.username)}</AvatarFallback>
            </Avatar>
          </Button>
        ) : (
          <Button variant="ghost" className="h-9 gap-2 px-1.5 pr-2" aria-label="Account menu">
            <Avatar size="sm">
              <AvatarFallback>{initials(me.label || me.username)}</AvatarFallback>
            </Avatar>
            <span className="hidden max-w-[140px] truncate text-sm font-medium md:inline">{me.label || me.username}</span>
            <ChevronsUpDown className="hidden size-3.5 text-muted-foreground md:inline" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-3 py-2 font-normal">
          <Avatar>
            <AvatarFallback>{initials(me.label || me.username)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-foreground">{me.label || me.username}</div>
            <div className="truncate text-xs text-muted-foreground">{me.email ?? me.username}</div>
          </div>
        </DropdownMenuLabel>
        <div className="flex flex-wrap gap-1 px-2 pb-2">
          <Badge variant="secondary">{roleLabel}</Badge>
          {me.companyLabel && <Badge variant="outline">{me.companyLabel}</Badge>}
          {me.mfaEnabled && (
            <Badge variant="success">
              <ShieldCheck /> MFA
            </Badge>
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/account" search={{ tab: "profile" } as never}>
              <UserRound /> Account
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/account" search={{ tab: "preferences" } as never}>
              <Settings2 /> Preferences
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/account" search={{ tab: "security" } as never}>
              <KeyRound /> Security
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => void signOut()}>
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default UserMenu;
